import { useState } from 'react';
import { Modal, ModalContent, Button, Control, Label, TextArea, LockIcon } from '@forge/common';
import { useApiMutation } from '@/hooks/useApiMutation';
import { sftpUsersApi } from '@/api/sftpUsers';
import type { SftpUser, UpdateSftpUserPayload } from '@/api/sftpUsers';

interface RotateKeyDialogProps {
  sftpUser: SftpUser;
  onClose: () => void;
  onSuccess: () => void;
}

const RotateKeyDialog = ({ sftpUser, onClose, onSuccess }: RotateKeyDialogProps) => {
  const [publicKey, setPublicKey] = useState('');

  const { mutate: rotate, isSubmitting } = useApiMutation<UpdateSftpUserPayload, SftpUser>({
    mutationFn: (data) => sftpUsersApi.update(sftpUser.id, data),
    successMessage: 'SSH key rotated successfully',
    errorMessage: 'Failed to rotate SSH key',
    onSuccess: () => onSuccess(),
  });

  // OpenSSH public keys start with a known algorithm prefix; guard against
  // someone pasting a private key or an unrelated blob.
  const looksLikePublicKey = /^(ssh-rsa|ssh-ed25519|ecdsa-sha2-\S+)\s+\S+/.test(publicKey.trim());
  const isValid = looksLikePublicKey;

  const handleSubmit = async () => {
    if (!isValid) return;
    await rotate({ publicKey: publicKey.trim() });
  };

  return (
    <Modal
      title="Rotate SSH Key"
      onModalClose={onClose}
      onBackdropClick={onClose}
      footerLeft={
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
      }
      footerRight={
        <Button
          variant="primary"
          icon={<LockIcon />}
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? 'Rotating...' : 'Rotate Key'}
        </Button>
      }
    >
      <ModalContent padded>
        <div className="flex flex-col gap-3">
          <p className="text-sm text-color-secondary">
            Replace the SSH public key for{' '}
            <span className="font-bold">{sftpUser.username}</span>
            {sftpUser.clientName ? (
              <>
                {' '}
                (<span className="font-bold">{sftpUser.clientName}</span>)
              </>
            ) : undefined}
            . The previous key stops working immediately.
          </p>

          <Control hideSpacing="bottom">
            <Label htmlFor="rotate-public-key">New SSH Public Key</Label>
            <TextArea
              id="rotate-public-key"
              rows={4}
              placeholder="ssh-ed25519 AAAA... user@host"
              value={publicKey}
              onChange={(e) => setPublicKey(e.target.value)}
              required
            />
            {publicKey.trim() && !looksLikePublicKey && (
              <p className="mt-1 text-xs text-color-error">
                This does not look like an OpenSSH public key. Paste the contents of the
                client&apos;s <span className="font-mono">.pub</span> file, not a private key.
              </p>
            )}
          </Control>

          <p className="text-xs text-color-tertiary">
            Only the client holds the matching private key. Make sure they have generated the
            new keypair before rotating.
          </p>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default RotateKeyDialog;
